import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { LoadingSkeleton } from '@/components/shared/LoadingSkeleton';
import { DEMO_LOGIN_ENABLED } from '@/config/demoLogin';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
  /**
   * When true, a user whose account is linked to a person entity
   * (`isLinkedPerson`) is granted access even if they do not hold any of
   * `requiredRoles`. This implements the additive 'My CIF' self-service access
   * agreed in #315: CIF/HR/Comptroller/Finance users who are also linked persons
   * can reach their personal views without a role switcher.
   */
  allowLinkedPerson?: boolean;
  /**
   * Roles that must NEVER reach this route, even though they satisfy
   * `requiredRoles` (e.g. comptroller/finance/cif all carry the base 'user'
   * role). Checked against the raw role array (`user.roles`), not
   * `hasAnyRole`/`isEffectiveRole`, so it cannot be bypassed by
   * frontwindow-combined accounts the way an effective-role check could be
   * (see migration 405's write-guard role set, which this mirrors) (#1072).
   */
  excludeRoles?: string[];
  fallback?: React.ReactNode;
}

/** Default landing page for front-window-only users when the current route does not list 'frontwindow'. */
export const FRONT_WINDOW_REDIRECT = '/customer-service' as const;

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  allowLinkedPerson = false,
  excludeRoles = [],
  fallback,
}) => {
  const {
    isAuthenticated,
    isLoading,
    user,
    hasAnyRole,
    isFrontWindowOnly,
    isLinkedPerson,
    isAdmin,
    isLinkedPersonLoading,
  } = useAuth();
  const location = useLocation();
  const fallbackElement = fallback ?? <Navigate to="/login" replace />;

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (!isAuthenticated) {
    const targetRoute = `${location.pathname}${location.search}${location.hash ?? ''}`;
    if (targetRoute && targetRoute !== '/login') {
      localStorage.setItem('intended_route', targetRoute);
    }
    return <>{fallbackElement}</>;
  }

  // Wait for user data to be hydrated before evaluating roles — without this
  // guard, hasAnyRole checks against an empty array and redirects to /unauthorized.
  if (!user) {
    return <LoadingSkeleton />;
  }

  if (allowLinkedPerson && isLinkedPersonLoading) {
    return <LoadingSkeleton />;
  }

  // Additive linked-person access: grant entry to 'My CIF' personal routes for
  // any user whose account is linked to a person entity, regardless of their
  // operational effective role (implements the #315 additive design). Checked
  // before the frontwindow-only restriction below so that Front Window users
  // who are also linked persons can reach their own hand receipt, notifications,
  // and transactions views (#1010) instead of being redirected away first.
  if (allowLinkedPerson && isLinkedPerson) {
    return <>{children}</>;
  }

  // frontwindow users can only access routes that explicitly list 'frontwindow'
  if (isFrontWindowOnly && !requiredRoles.includes('frontwindow')) {
    return <Navigate to={FRONT_WINDOW_REDIRECT} replace />;
  }

  if (requiredRoles.length > 0) {
    if (!hasAnyRole(requiredRoles)) {
      // Admin users blocked on service-member routes are redirected to the
      // impersonation page rather than a raw 403 (#1000).
      if (isAdmin && requiredRoles.includes('service-member') && DEMO_LOGIN_ENABLED) {
        return <Navigate to="/service-member-login" replace />;
      }
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Raw-role exclusion: blocks comptroller/finance/cif even though they carry
  // the base 'user' role and would otherwise satisfy requiredRoles above (#1072).
  if (excludeRoles.length > 0 && (user.roles ?? []).some((role) => excludeRoles.includes(role))) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};
